import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Baby, Check, ChevronDown, Loader2, Mic, Plus, Scissors, Stethoscope } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { AudioRecorder } from '../components/AudioRecorder';
import { CodeItem } from '../components/CodeSelector';
import { useIcd10Cache, useNhrplCache } from '../hooks/useCodeCache';
import { SOAPNoteEditor } from '../components/clinical/SOAPNoteEditor';
import { ManualNoteForm } from '../components/clinical/ManualNoteForm';
import { NewObsConsultForm } from '../components/clinical/NewObsConsultForm';
import { NewPregnancyModal } from '../components/NewPregnancyModal';
import { SurgeryForm } from '../components/clinical/SurgeryForm';
import { SurgeryCard } from '../components/clinical/SurgeryCard';
import { VaginalDeliveryForm } from '../components/clinical/VaginalDeliveryForm';
import { VaginalDeliveryCard } from '../components/clinical/VaginalDeliveryCard';
import { PrescriptionSection } from '../components/clinical/PrescriptionSection';
import { ReportSection } from '../components/clinical/ReportSection';
import { NoteCard } from '../components/clinical/NoteCard';
import type {
  BabySex, ClinicalNote, DeliveryMode, FormularyItem, Hospital, Prescription, ProcedureTemplate,
  SuggestedPrescription, Surgery, TranscribeResult, UterotonicOption, VaginalDelivery,
} from '../types/clinical';
import type { Pregnancy } from '../types/pregnancy';
import { clinicalReducer, initialState } from './ClinicalPage/reducer';

export function ClinicalPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { get, post, patch, del } = useApi();
  const { userRole } = useAuth();
  const isDoctor = userRole === 'doctor';
  const { search: searchIcd10, loading: icd10Loading } = useIcd10Cache();
  const { search: searchNhrpl, loading: nhrplLoading } = useNhrplCache();

  // ── Reducer (form state — 82 hooks collapsed) ──
  const [s, dispatch] = useReducer(clinicalReducer, initialState);

  // ── Independent useState hooks (data collections + page-level UI) ──
  const [notes, setNotes] = useState<ClinicalNote[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [deliveries, setDeliveries] = useState<VaginalDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [formulary, setFormulary] = useState<FormularyItem[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [templates, setTemplates] = useState<ProcedureTemplate[]>([]);
  const [reports, setReports] = useState<{ id: string; templateType: string; reportText: string; pdfGcsPath?: string; createdAt: string }[]>([]);
  const [error, setError] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientDob, setPatientDob] = useState('');
  const [patientBloodGroup, setPatientBloodGroup] = useState('');
  const [patientRhFactor, setPatientRhFactor] = useState('');
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [deliveryPdfLoadingId, setDeliveryPdfLoadingId] = useState<string | null>(null);
  const [activePregnancies, setActivePregnancies] = useState<Pregnancy[]>([]);
  const [linkedPregnancyId, setLinkedPregnancyId] = useState<string | null>(null);
  const [showNewPregnancyModal, setShowNewPregnancyModal] = useState(false);
  const [showObsPrompt, setShowObsPrompt] = useState(false);
  const [showObsPicker, setShowObsPicker] = useState(false);
  const [expandedObsNotes, setExpandedObsNotes] = useState<Set<string>>(new Set());
  const [activeSection, setActiveSection] = useState('section-gynae-notes');

  // Ref to hold current form state — avoids re-creating callbacks on every keystroke
  const formRef = useRef({
    subjective: s.subjective, objective: s.objective, assessment: s.assessment, plan: s.plan,
    icd10Suggested: s.icd10Suggested, icd10Confirmed: s.icd10Confirmed,
    nhrplCodes: s.nhrplCodes, summary: s.summary, followUp: s.followUp,
    bp: s.bp, weight: s.weight, audioBlob: s.audioBlob,
    hospitalId: s.hospitalId, manualTitle: s.manualTitle, manualContent: s.manualContent, manualDate: s.manualDate,
  });
  formRef.current = {
    subjective: s.subjective, objective: s.objective, assessment: s.assessment, plan: s.plan,
    icd10Suggested: s.icd10Suggested, icd10Confirmed: s.icd10Confirmed,
    nhrplCodes: s.nhrplCodes, summary: s.summary, followUp: s.followUp,
    bp: s.bp, weight: s.weight, audioBlob: s.audioBlob,
    hospitalId: s.hospitalId, manualTitle: s.manualTitle, manualContent: s.manualContent, manualDate: s.manualDate,
  };

  // Load data
  useEffect(() => {
    if (!patientId) return;
    (async () => {
      setLoading(true);
      try {
        const [notesRes, rxRes, surgRes, deliveryRes, formRes, hospRes, tmplRes, patRes, rptRes, pregRes] = await Promise.all([
          get<ClinicalNote[]>(`/clinical/${patientId}/notes`),
          get<Prescription[]>(`/clinical/${patientId}/prescriptions`),
          get<Surgery[]>(`/clinical/${patientId}/surgeries`),
          get<VaginalDelivery[]>(`/clinical/${patientId}/vaginal-deliveries`),
          get<FormularyItem[]>('/formulary'),
          get<Hospital[]>('/config/hospitals'),
          get<ProcedureTemplate[]>('/procedure-templates'),
          get<{ firstName: string; lastName: string; dateOfBirth: string; obstetricHistory?: { bloodGroup?: string; rhFactor?: string } }>(`/patients/${patientId}`),
          get<{ id: string; templateType: string; reportText: string; pdfGcsPath?: string; createdAt: string }[]>(`/clinical/${patientId}/reports`),
          get<Pregnancy[]>(`/patients/${patientId}/pregnancies`),
        ]);
        if (notesRes.success && notesRes.data) setNotes(notesRes.data);
        if (rxRes.success && rxRes.data) setPrescriptions(rxRes.data);
        if (surgRes.success && surgRes.data) setSurgeries(surgRes.data);
        if (deliveryRes.success && deliveryRes.data) setDeliveries(deliveryRes.data);
        if (formRes.success && formRes.data) setFormulary(formRes.data);
        if (hospRes.success && hospRes.data) setHospitals(hospRes.data.filter(h => h.active));
        if (tmplRes.success && tmplRes.data) setTemplates(tmplRes.data);
        if (rptRes.success && rptRes.data) setReports(rptRes.data);
        if (pregRes.success && pregRes.data) setActivePregnancies(pregRes.data.filter(p => p.status === 'active'));
        if (patRes.success && patRes.data) {
          const p = patRes.data;
          setPatientName(`${p.firstName || ''} ${p.lastName || ''}`.trim());
          setPatientDob(p.dateOfBirth || '');
          setPatientBloodGroup(p.obstetricHistory?.bloodGroup || '');
          setPatientRhFactor(p.obstetricHistory?.rhFactor || '');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [patientId, get]);

  // ── Auto-save draft + restore + navigation guard ──────────────────

  const [draftPrompt, setDraftPrompt] = useState<Record<string, unknown> | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState('');
  // hasUnsavedWork is derived above from reducer state
  const hasUnsavedWork = s.editing || s.showManualNote || s.showObsForm || s.showSurgeryForm || s.showDeliveryForm || s.showReportForm || s.showRxForm;

  // Check for existing draft on page load
  useEffect(() => {
    if (!patientId || !isDoctor) return;
    (async () => {
      const res = await get<{ formState: Record<string, unknown>; updatedAt: string } | null>(`/clinical/${patientId}/draft`);
      if (res.success && res.data?.formState) {
        setDraftPrompt(res.data.formState);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Auto-open forms when navigated from PregnancyDetailPage
  useEffect(() => {
    const navState = location.state as {
      openObsForm?: boolean;
      openDeliveryForm?: boolean;
      openSurgeryForm?: boolean;
      pregnancyId?: string;
    } | null;
    if (!navState) return;
    const pid = navState.pregnancyId || null;
    if (navState.openObsForm && pid) {
      dispatch({ type: 'OPEN_OBS_FORM', pregnancyId: pid });
    } else if (navState.openDeliveryForm) {
      setLinkedPregnancyId(pid);
      dispatch({ type: 'OPEN_DELIVERY_FORM' });
    } else if (navState.openSurgeryForm) {
      setLinkedPregnancyId(pid);
      dispatch({ type: 'OPEN_SURGERY_FORM' });
    }
    // Clear state so it doesn't re-open on refresh
    window.history.replaceState({}, document.title);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restoreDraft = useCallback(() => {
    if (!draftPrompt) return;
    if (draftPrompt.subjective !== undefined) {
      dispatch({ type: 'RESTORE_SOAP_DRAFT', draft: draftPrompt });
    } else if (draftPrompt.manualTitle !== undefined) {
      dispatch({ type: 'RESTORE_MANUAL_DRAFT', draft: draftPrompt });
    }
    setDraftPrompt(null);
  }, [draftPrompt]);

  const dismissDraft = useCallback(async () => {
    setDraftPrompt(null);
    if (patientId) {
      await del(`/clinical/${patientId}/draft`).catch(() => {});
    }
  }, [patientId, del]);

  // Auto-save draft every 20 seconds while editing — reads from formRef to avoid
  // recreating the interval on every keystroke (was 17 deps, now 4)
  const postRef = useRef(post);
  postRef.current = post;

  useEffect(() => {
    if (!patientId || !isDoctor) return;
    if (!s.editing && !s.showManualNote) return;

    const interval = setInterval(async () => {
      const f = formRef.current;
      let formState: Record<string, unknown>;
      if (s.editing) {
        formState = {
          type: 'soap',
          subjective: f.subjective, objective: f.objective, assessment: f.assessment,
          plan: f.plan, summary: f.summary, followUp: f.followUp,
          bp: f.bp, weight: f.weight,
          icd10Confirmed: f.icd10Confirmed, nhrplCodes: f.nhrplCodes,
          hospitalId: f.hospitalId, icd10Suggested: f.icd10Suggested,
        };
      } else {
        formState = {
          type: 'manual',
          manualTitle: f.manualTitle, manualContent: f.manualContent, manualDate: f.manualDate,
        };
      }

      try {
        const res = await postRef.current<{ savedAt: string }>(`/clinical/${patientId}/draft`, formState);
        if (res.success && res.data) {
          setLastSavedAt(new Date(res.data.savedAt).toLocaleTimeString());
        }
      } catch {
        // Silent — auto-save should never disrupt workflow
      }
    }, 20000);

    return () => clearInterval(interval);
  }, [patientId, isDoctor, s.editing, s.showManualNote]);

  // Delete draft after successful save
  const clearDraft = useCallback(async () => {
    if (patientId) {
      await del(`/clinical/${patientId}/draft`).catch(() => {});
      setLastSavedAt('');
    }
  }, [patientId, del]);

  // Browser close / refresh guard
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedWork]);

  // Handle audio ready from recorder
  const handleAudioReady = useCallback(async (blob: Blob) => {
    if (!patientId) return;
    dispatch({ type: 'SET_AUDIO_BLOB', blob });
    dispatch({ type: 'SET_TRANSCRIBING', step: 'transcribing' });
    setError('');

    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';

    const formData = new FormData();
    formData.append('audio', blob, `consultation.${ext}`);

    // Switch to "structuring" step after ~4s (Whisper typically finishes by then)
    const stepTimer = setTimeout(() => dispatch({ type: 'SET_TRANSCRIBING', step: 'structuring' }), 4000);

    try {
      const res = await post<TranscribeResult>(`/clinical/${patientId}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.success && res.data) {
        dispatch({ type: 'LOAD_TRANSCRIPTION', result: res.data });
      } else {
        setError(res.error || 'Transcription failed');
      }
    } catch {
      setError('Failed to connect to transcription service');
    } finally {
      clearTimeout(stepTimer);
      dispatch({ type: 'SET_TRANSCRIBING', step: null });
    }
  }, [patientId, post]);

  // Save SOAP note — uses formRef to avoid re-creating on every keystroke
  const handleSaveNote = useCallback(async () => {
    if (!patientId) return;
    dispatch({ type: 'SET_SOAP_SAVING', saving: true });
    setError('');

    const f = formRef.current;
    const result = await post<ClinicalNote>(`/clinical/${patientId}/notes`, {
      subjective: f.subjective,
      objective: f.objective,
      assessment: f.assessment,
      plan: f.plan,
      icd10Suggested: f.icd10Suggested,
      icd10Confirmed: f.icd10Confirmed,
      nhrplCodes: f.nhrplCodes,
      consumables: [],
      summary: f.summary,
      followUp: f.followUp,
      vitalSigns: { bp: f.bp, weight: f.weight },
      rawTranscript: '',
      audioGcsPath: '',
      appointmentId: '',
      hospitalId: f.hospitalId,
      hospitalName: hospitals.find(h => h.id === f.hospitalId)?.name || '',
    });

    if (result.success && result.data) {
      // Upload audio if available
      if (f.audioBlob && result.data.id) {
        const audioForm = new FormData();
        audioForm.append('audio', f.audioBlob, 'consultation.webm');
        try {
          await postRef.current(`/clinical/${patientId}/notes/${result.data.id}/upload-audio`, audioForm, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          // Non-critical — note saved, audio upload failed
        }
      }

      setNotes(prev => [result.data!, ...prev]);
      dispatch({ type: 'CLOSE_SOAP_EDITOR' });
      dispatch({ type: 'RESET_SOAP_FORM' });
      clearDraft();
    } else {
      setError(result.error || 'Failed to save note');
    }
    dispatch({ type: 'SET_SOAP_SAVING', saving: false });
  }, [patientId, post]);

  // Save manual note
  const handleSaveManualNote = useCallback(async () => {
    if (!patientId || !s.manualTitle || !s.manualContent) return;
    dispatch({ type: 'SET_MANUAL_SAVING', saving: true });
    setError('');
    const result = await post<ClinicalNote>(`/clinical/${patientId}/manual-note`, {
      title: s.manualTitle,
      content: s.manualContent,
      date: s.manualDate,
    });
    if (result.success && result.data) {
      setNotes(prev => [result.data!, ...prev]);
      dispatch({ type: 'CLOSE_MANUAL_NOTE' });
      clearDraft();
    } else {
      setError(result.error || 'Failed to save note');
    }
    dispatch({ type: 'SET_MANUAL_SAVING', saving: false });
  }, [patientId, s.manualTitle, s.manualContent, s.manualDate, post]);

  // Accept suggested prescription — create it
  const handleAcceptPrescription = useCallback(async (rx: SuggestedPrescription) => {
    if (!patientId) return;
    const result = await post<Prescription>(`/clinical/${patientId}/prescriptions`, {
      drugName: rx.drugName,
      dose: rx.dose,
      frequency: rx.frequency,
      duration: rx.duration,
      quantity: rx.quantity,
      repeats: rx.repeats,
    });
    if (result.success && result.data) {
      setPrescriptions(prev => [result.data!, ...prev]);
      dispatch({ type: 'ACCEPT_PRESCRIPTION', drugName: rx.drugName });
    } else {
      setError(result.error || 'Failed to accept prescription');
    }
  }, [patientId, post]);

  // Manual prescription
  const handleCreatePrescription = useCallback(async () => {
    if (!patientId || !s.rxDrug) return;
    const result = await post<Prescription>(`/clinical/${patientId}/prescriptions`, {
      drugName: s.rxDrug,
      dose: s.rxDose,
      frequency: s.rxFreq,
      duration: s.rxDuration,
      quantity: s.rxQty,
      repeats: s.rxRepeats,
    });
    if (result.success && result.data) {
      setPrescriptions(prev => [result.data!, ...prev]);
      dispatch({ type: 'RESET_RX_FORM' });
    } else {
      setError(result.error || 'Failed to create prescription');
    }
  }, [patientId, s.rxDrug, s.rxDose, s.rxFreq, s.rxDuration, s.rxQty, s.rxRepeats, post]);

  // Generate PDF
  const handleGeneratePdf = useCallback(async (rxId: string) => {
    if (!patientId) return;
    setPdfLoading(rxId);
    setPdfError(null);
    try {
      const result = await post<{ pdfUrl: string }>(`/clinical/${patientId}/prescriptions/${rxId}/pdf`, {});
      if (result.success && result.data?.pdfUrl) {
        window.open(result.data.pdfUrl, '_blank', 'noopener,noreferrer');
        setPrescriptions(prev => prev.map(p => p.id === rxId ? { ...p, status: 'approved' as const } : p));
      } else {
        setPdfError(result.error || 'Failed to generate PDF. Please try again.');
      }
    } catch {
      setPdfError('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(null);
    }
  }, [patientId, post]);

  // Surgery
  const handleCreateSurgery = useCallback(async () => {
    if (!patientId || !s.surgProcedure) return;
    const anaesthesiaType = s.surgAnaesthesiaType === 'Other' ? s.surgAnaesthesiaOther : s.surgAnaesthesiaType;
    const result = await post<Surgery>(`/clinical/${patientId}/surgeries`, {
      procedure: s.surgProcedure,
      hospital: s.surgHospital,
      date: s.surgDate,
      ebl: s.surgEbl,
      anaesthesiaType,
      anaesthetist: s.surgAnaesthetist,
      caseType: s.surgCaseType,
      surgicalAssistant: s.surgSurgicalAssistant,
      indications: s.surgIndications,
      procedureNotes: s.surgProcedureNotes,
      findings: s.surgFindings,
      complications: s.surgComplications,
      postOpInstructions: s.surgPostOp,
      icd10Codes: s.surgIcd10.map(c => c.code),
      ...(linkedPregnancyId ? { pregnancyId: linkedPregnancyId } : {}),
    });
    if (result.success && result.data) {
      setSurgeries(prev => [result.data!, ...prev]);
      dispatch({ type: 'RESET_SURGERY_FORM' });
      setLinkedPregnancyId(null);
    } else {
      setError(result.error || 'Failed to save surgery');
    }
  }, [patientId, s.surgProcedure, s.surgHospital, s.surgDate, s.surgEbl, s.surgAnaesthesiaType, s.surgAnaesthesiaOther, s.surgAnaesthetist, s.surgCaseType, s.surgSurgicalAssistant, s.surgIndications, s.surgProcedureNotes, s.surgFindings, s.surgComplications, s.surgPostOp, s.surgIcd10, post]);

  // Vaginal Delivery
  const handleCreateDelivery = useCallback(async () => {
    if (!patientId || !s.vdDate) return;
    dispatch({ type: 'SET_DELIVERY_SAVING', saving: true });
    const result = await post<VaginalDelivery>(`/clinical/${patientId}/vaginal-deliveries`, {
      date: s.vdDate,
      time: s.vdTime,
      hospital: s.vdHospital,
      doctorName: s.vdDoctorName,
      doctorEmail: s.vdDoctorEmail,
      paediatrician: s.vdPaediatrician,
      wardSister: s.vdWardSister,
      umbilicalCordPh: s.vdUmbilicalCordPh,
      history: s.vdHistory,
      deliveryMode: s.vdDeliveryMode,
      uterotonic: s.vdUterotonic,
      uterotonicOther: s.vdUterotonicOther,
      notes: s.vdNotes,
      postPartum: s.vdPostPartum,
      babyWeight: s.vdBabyWeight,
      babySex: s.vdBabySex,
      apgar1min: s.vdApgar1min,
      apgar5min: s.vdApgar5min,
      bcgGiven: s.vdBcgGiven,
      polioGiven: s.vdPolioGiven,
      ...(linkedPregnancyId ? { pregnancyId: linkedPregnancyId } : {}),
    });
    if (result.success && result.data) {
      setDeliveries(prev => [result.data!, ...prev]);
      dispatch({ type: 'RESET_DELIVERY_FORM' });
      setLinkedPregnancyId(null);
    } else {
      setError(result.error || 'Failed to save delivery');
    }
    dispatch({ type: 'SET_DELIVERY_SAVING', saving: false });
  }, [
    patientId, s.vdDate, s.vdTime, s.vdHospital, s.vdDoctorName, s.vdDoctorEmail, s.vdPaediatrician,
    s.vdWardSister, s.vdUmbilicalCordPh, s.vdHistory, s.vdDeliveryMode,
    s.vdUterotonic, s.vdUterotonicOther, s.vdNotes, s.vdPostPartum,
    s.vdBabyWeight, s.vdBabySex, s.vdApgar1min, s.vdApgar5min, s.vdBcgGiven, s.vdPolioGiven, post,
  ]);

  const handleSaveDeliveryEdit = useCallback(async () => {
    if (!patientId || !s.editingDeliveryId) return;
    dispatch({ type: 'SET_EDIT_SAVING', saving: true });
    const res = await patch<Partial<VaginalDelivery>>(
      `/clinical/${patientId}/vaginal-deliveries/${s.editingDeliveryId}`,
      s.editingDeliveryData,
    );
    if (res.success && res.data) {
      setDeliveries(prev => prev.map(v => v.id === s.editingDeliveryId ? { ...v, ...res.data } as VaginalDelivery : v));
      dispatch({ type: 'CANCEL_EDIT_DELIVERY' });
    } else {
      setError(res.error || 'Failed to save delivery edit');
    }
    dispatch({ type: 'SET_EDIT_SAVING', saving: false });
  }, [patientId, s.editingDeliveryId, s.editingDeliveryData, patch]);

  const handleGenerateDeliveryPdf = useCallback(async (deliveryId: string) => {
    if (!patientId) return;
    setDeliveryPdfLoadingId(deliveryId);
    try {
      const res = await post<{ pdfUrl: string }>(
        `/clinical/${patientId}/vaginal-deliveries/${deliveryId}/report`,
        {},
      );
      if (res.success && res.data?.pdfUrl) {
        window.open(res.data.pdfUrl, '_blank', 'noopener,noreferrer');
      } else {
        setError(res.error || 'Failed to generate delivery PDF');
      }
    } finally {
      setDeliveryPdfLoadingId(null);
    }
  }, [patientId, post]);

  // ── Inline edit handlers ────────────────────────────────────────────

  const handleSaveNoteEdit = useCallback(async () => {
    if (!patientId || !s.editingNoteId) return;
    dispatch({ type: 'SET_EDIT_SAVING', saving: true });
    const res = await patch<Partial<ClinicalNote>>(`/clinical/${patientId}/notes/${s.editingNoteId}/edit`, s.editingNoteData);
    if (res.success && res.data) {
      setNotes(prev => prev.map(n => n.id === s.editingNoteId ? { ...n, ...res.data } as ClinicalNote : n));
      dispatch({ type: 'CANCEL_EDIT_NOTE' });
    } else {
      setError(res.error || 'Failed to save note edit');
    }
    dispatch({ type: 'SET_EDIT_SAVING', saving: false });
  }, [patientId, s.editingNoteId, s.editingNoteData, patch]);

  const handleSaveSurgEdit = useCallback(async () => {
    if (!patientId || !s.editingSurgId) return;
    dispatch({ type: 'SET_EDIT_SAVING', saving: true });
    const res = await patch<Partial<Surgery>>(`/clinical/${patientId}/surgeries/${s.editingSurgId}`, s.editingSurgData);
    if (res.success && res.data) {
      setSurgeries(prev => prev.map(sg => sg.id === s.editingSurgId ? { ...sg, ...res.data } as Surgery : sg));
      dispatch({ type: 'CANCEL_EDIT_SURGERY' });
    } else {
      setError(res.error || 'Failed to save surgery edit');
    }
    dispatch({ type: 'SET_EDIT_SAVING', saving: false });
  }, [patientId, s.editingSurgId, s.editingSurgData, patch]);

  const handleSaveRxEdit = useCallback(async () => {
    if (!patientId || !s.editingRxId) return;
    dispatch({ type: 'SET_EDIT_SAVING', saving: true });
    const res = await patch<Partial<Prescription>>(`/clinical/${patientId}/prescriptions/${s.editingRxId}/edit`, s.editingRxData);
    if (res.success && res.data) {
      setPrescriptions(prev => prev.map(p => p.id === s.editingRxId ? { ...p, ...res.data } as Prescription : p));
      dispatch({ type: 'CANCEL_EDIT_RX' });
    } else {
      setError(res.error || 'Failed to save prescription edit');
    }
    dispatch({ type: 'SET_EDIT_SAVING', saving: false });
  }, [patientId, s.editingRxId, s.editingRxData, patch]);

  // Generate report draft via Gemini
  const handleDraftReport = useCallback(async () => {
    if (!patientId || !s.reportFindings.trim()) return;
    dispatch({ type: 'SET_REPORT_DRAFTING', drafting: true });
    const res = await post<{ draftText: string }>(`/clinical/${patientId}/reports/draft`, {
      templateType: s.reportType,
      findings: s.reportFindings,
      patientId,
    });
    if (res.success && res.data) {
      dispatch({ type: 'SET_REPORT_FIELD', field: 'reportDraft', value: res.data.draftText });
    } else {
      setError(res.error || 'Failed to generate report draft');
    }
    dispatch({ type: 'SET_REPORT_DRAFTING', drafting: false });
  }, [patientId, s.reportType, s.reportFindings, post]);

  // Save report + generate PDF
  const handleSaveReport = useCallback(async () => {
    if (!patientId || !s.reportDraft.trim()) return;
    dispatch({ type: 'SET_REPORT_SAVING', saving: true });
    const res = await post<{ id: string; templateType: string; reportText: string; pdfUrl: string; createdAt: string }>(`/clinical/${patientId}/reports`, {
      templateType: s.reportType,
      reportText: s.reportDraft,
      patientId,
    });
    if (res.success && res.data) {
      setReports(prev => [res.data!, ...prev]);
      if (res.data.pdfUrl) window.open(res.data.pdfUrl, '_blank', 'noopener,noreferrer');
      dispatch({ type: 'CLOSE_REPORT_FORM' });
    } else {
      setError(res.error || 'Failed to save report');
    }
    dispatch({ type: 'SET_REPORT_SAVING', saving: false });
  }, [patientId, s.reportType, s.reportDraft, post]);

  // Generate invoice from note
  const handleGenerateInvoice = useCallback(async (noteId: string) => {
    if (!patientId) return;
    const result = await post<{ patientId: string; clinicalNoteId: string; icd10Codes: string[]; nhrplCodes: string[] }>(
      `/clinical/${patientId}/notes/${noteId}/generate-invoice`,
      {}
    );
    if (result.success && result.data) {
      const params = new URLSearchParams({
        patientId: result.data.patientId,
        clinicalNoteId: result.data.clinicalNoteId,
        icd10: result.data.icd10Codes.join(','),
        nhrpl: result.data.nhrplCodes.join(','),
      });
      navigate(`/billing/new?${params.toString()}`);
    } else {
      setError(result.error || 'Failed to generate invoice');
    }
  }, [patientId, post, navigate]);

  // Create pregnancy from the inline modal
  const handleCreatePregnancy = useCallback(async (data: Record<string, unknown>) => {
    if (!patientId) return;
    const res = await post<Pregnancy>(`/patients/${patientId}/pregnancies`, data);
    if (res.success && res.data) {
      setActivePregnancies(prev => [res.data!, ...prev]);
      setShowNewPregnancyModal(false);
    } else {
      throw new Error(res.error || 'Failed to create pregnancy');
    }
  }, [patientId, post]);

  // ── Note splitting ────────────────────────────────────────────────
  const gynaecNotes = notes.filter(n => n.noteType !== 'obstetric');
  const obstetricNotes = notes.filter(n => n.noteType === 'obstetric');

  const toggleObsNote = useCallback((id: string) => {
    setExpandedObsNotes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // ── Sticky nav IntersectionObserver ─────────────────────────────
  useEffect(() => {
    if (loading) return;
    const sections = document.querySelectorAll('[data-nav-section]');
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        }
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );
    sections.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  const navItems = [
    ...(isDoctor ? [{ id: 'section-ambient-scribe', label: 'Ambient Scribe' }] : []),
    { id: 'section-gynae-notes', label: 'Gynae Notes' },
    { id: 'section-obstetric-notes', label: 'Obstetric Notes' },
    { id: 'section-prescriptions', label: 'Prescriptions' },
    { id: 'section-surgeries', label: 'Surgeries' },
    { id: 'section-deliveries', label: 'Deliveries' },
    { id: 'section-reports', label: 'Reports' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <nav className="text-xs text-gray-500 mb-2 flex items-center gap-1">
        <button onClick={() => navigate('/dashboard')} className="hover:text-blue-600">Dashboard</button>
        <span>›</span>
        <button onClick={() => navigate('/patients')} className="hover:text-blue-600">Patients</button>
        <span>›</span>
        {patientName && (
          <>
            <button onClick={() => navigate(`/patients/${patientId}`)} className="hover:text-blue-600">{patientName}</button>
            <span>›</span>
          </>
        )}
        <button onClick={() => { navigate(`/clinical/${patientId}`); window.scrollTo(0, 0); }} className="text-gray-700 hover:text-blue-600">Clinical</button>
      </nav>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Clinical History</h1>
          {patientName && (
            <p className="text-sm text-gray-500 mt-0.5">
              {patientName}
              {patientDob && <span className="ml-2 text-gray-400">DOB: {patientDob}</span>}
            </p>
          )}
        </div>
        {isDoctor && !s.editing && !s.showManualNote && !s.showObsForm && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => dispatch({ type: 'OPEN_SOAP_EDITOR', showRecorder: true })}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> New Gynae Consult
            </button>
            <button
              onClick={() => {
                if (activePregnancies.length === 1) {
                  dispatch({ type: 'OPEN_OBS_FORM', pregnancyId: activePregnancies[0].pregnancyId });
                  setShowObsPrompt(false);
                  setShowObsPicker(false);
                } else if (activePregnancies.length > 1) {
                  setShowObsPicker(true);
                  setShowObsPrompt(false);
                } else {
                  setShowObsPrompt(true);
                  setShowObsPicker(false);
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 flex items-center gap-2"
            >
              <Baby className="h-4 w-4" /> New Obs Consult
            </button>
            <button
              onClick={() => setShowNewPregnancyModal(true)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> New Pregnancy
            </button>
            <button
              onClick={() => dispatch({ type: 'OPEN_MANUAL_NOTE' })}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> Manual Note
            </button>
          </div>
        )}
      </div>

      {/* Draft restore prompt */}
      {draftPrompt && (
        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-900">Unsaved draft found</p>
              <p className="text-xs text-amber-700 mt-0.5">You have an unsaved {draftPrompt.type === 'manual' ? 'manual note' : 'clinical note'} for this patient. Would you like to restore it?</p>
            </div>
            <div className="flex gap-2 ml-4">
              <button onClick={restoreDraft} className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700">Restore Draft</button>
              <button onClick={dismissDraft} className="px-3 py-1.5 text-xs text-amber-700 hover:text-amber-900">Discard</button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-save indicator */}
      {lastSavedAt && hasUnsavedWork && (
        <p className="text-xs text-gray-400 mb-2">Draft auto-saved at {lastSavedAt}</p>
      )}

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {/* ── Two-column layout: sticky nav + content ── */}
      <div className="flex gap-6">
        {/* Left nav — hidden on mobile */}
        <nav className="hidden md:block w-44 shrink-0">
          <div className="sticky top-6 space-y-0.5">
            {navItems.map(item => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={e => { e.preventDefault(); document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' }); }}
                className={`block px-3 py-1.5 text-xs rounded-r transition-colors ${activeSection === item.id ? 'border-l-2 border-teal-600 text-teal-700 font-medium bg-teal-50' : 'border-l-2 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              >
                {item.label}
              </a>
            ))}
            <div className="pt-2 mt-2 border-t border-gray-200">
              <button
                onClick={() => navigate(`/patients/${patientId}/pregnancies`)}
                className="block px-3 py-1.5 text-xs text-teal-600 hover:text-teal-800 font-medium"
              >
                Pregnancies &rarr;
              </button>
            </div>
          </div>
        </nav>

        {/* Right content column */}
        <div className="flex-1 min-w-0">

      {/* Ambient Scribe — Audio Recorder (shows when not editing, or when editing via New Consultation) */}
      {isDoctor && (!s.editing || s.showRecorder) && (
        <div id="section-ambient-scribe" data-nav-section className="mb-6">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Mic className="h-4 w-4" /> Ambient Scribe
          </h2>
          <AudioRecorder onAudioReady={handleAudioReady} disabled={s.transcribing} />
          {s.transcribing && (
            <div className="mt-3 space-y-1">
              <div className={`flex items-center gap-2 text-sm ${s.transcribeStep === 'transcribing' ? 'text-blue-600' : 'text-green-600'}`}>
                {s.transcribeStep === 'transcribing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {s.transcribeStep === 'transcribing' ? 'Transcribing audio...' : 'Audio transcribed'}
              </div>
              {s.transcribeStep === 'structuring' && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Loader2 className="h-4 w-4 animate-spin" /> Structuring SOAP note...
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Manual Note Form */}
      {s.showManualNote && (
        <ManualNoteForm
          manualDate={s.manualDate}
          setManualDate={(v: string) => dispatch({ type: 'SET_MANUAL_FIELD', field: 'manualDate', value: v })}
          manualTitle={s.manualTitle}
          setManualTitle={(v: string) => dispatch({ type: 'SET_MANUAL_FIELD', field: 'manualTitle', value: v })}
          manualContent={s.manualContent}
          setManualContent={(v: string) => dispatch({ type: 'SET_MANUAL_FIELD', field: 'manualContent', value: v })}
          manualSaving={s.manualSaving}
          onSave={handleSaveManualNote}
          onCancel={() => dispatch({ type: 'CLOSE_MANUAL_NOTE' })}
        />
      )}

      {/* SOAP Note Editor */}
      {s.editing && (
        <SOAPNoteEditor
          bp={s.bp} setBp={(v: string) => dispatch({ type: 'SET_VITAL', field: 'bp', value: v })}
          weight={s.weight} setWeight={(v: string) => dispatch({ type: 'SET_VITAL', field: 'weight', value: v })}
          hospitalId={s.hospitalId} setHospitalId={(v: string) => dispatch({ type: 'SET_HOSPITAL_ID', id: v })}
          hospitals={hospitals}
          subjective={s.subjective} setSubjective={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SOAP_FIELD', field: 'subjective', value: typeof v === 'function' ? v(s.subjective) : v })}
          objective={s.objective} setObjective={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SOAP_FIELD', field: 'objective', value: typeof v === 'function' ? v(s.objective) : v })}
          assessment={s.assessment} setAssessment={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SOAP_FIELD', field: 'assessment', value: typeof v === 'function' ? v(s.assessment) : v })}
          plan={s.plan} setPlan={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SOAP_FIELD', field: 'plan', value: typeof v === 'function' ? v(s.plan) : v })}
          summary={s.summary} setSummary={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SOAP_FIELD', field: 'summary', value: typeof v === 'function' ? v(s.summary) : v })}
          followUp={s.followUp} setFollowUp={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SOAP_FIELD', field: 'followUp', value: typeof v === 'function' ? v(s.followUp) : v })}
          templates={templates}
          nhrplCodes={s.nhrplCodes} setNhrplCodes={(v: React.SetStateAction<string[]>) => dispatch({ type: 'SET_NHRPL_CODES', codes: typeof v === 'function' ? v(s.nhrplCodes) : v })}
          searchNhrpl={searchNhrpl} nhrplLoading={nhrplLoading}
          icd10Suggested={s.icd10Suggested}
          icd10Confirmed={s.icd10Confirmed}
          setIcd10Confirmed={(v: React.SetStateAction<string[]>) => dispatch({ type: 'SET_ICD10_CONFIRMED', codes: typeof v === 'function' ? v(s.icd10Confirmed) : v })}
          searchIcd10={searchIcd10}
          icd10Loading={icd10Loading}
          suggestedPrescriptions={s.suggestedPrescriptions}
          setSuggestedPrescriptions={(v: React.SetStateAction<SuggestedPrescription[]>) => dispatch({ type: 'SET_SUGGESTED_PRESCRIPTIONS', prescriptions: typeof v === 'function' ? v(s.suggestedPrescriptions) : v })}
          onAcceptPrescription={handleAcceptPrescription}
          saving={s.soapSaving}
          onSave={handleSaveNote}
          onCancel={() => { dispatch({ type: 'CLOSE_SOAP_EDITOR' }); dispatch({ type: 'RESET_SOAP_FORM' }); }}
        />
      )}

      {/* Obs consult: no-pregnancy prompt */}
      {showObsPrompt && activePregnancies.length === 0 && (
        <div className="mb-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm flex items-center justify-between">
          <span className="text-amber-700">No active pregnancy. Create one first.{' '}
            <button onClick={() => navigate(`/patients/${patientId}/pregnancies`)} className="text-blue-600 hover:underline">Manage Pregnancies &rarr;</button>
          </span>
          <button onClick={() => setShowObsPrompt(false)} className="text-gray-400 hover:text-gray-600 ml-2">&times;</button>
        </div>
      )}

      {/* Obs consult: pregnancy picker (multiple active) */}
      {showObsPicker && activePregnancies.length > 1 && (
        <div className="mb-4 p-3 rounded bg-teal-50 border border-teal-200">
          <label className="block text-sm font-medium text-teal-800 mb-2">Select pregnancy for obstetric consultation:</label>
          <select
            defaultValue=""
            onChange={e => {
              if (e.target.value) {
                dispatch({ type: 'OPEN_OBS_FORM', pregnancyId: e.target.value });
                setShowObsPicker(false);
              }
            }}
            className="w-full max-w-sm px-2 py-1.5 text-sm border border-teal-300 rounded bg-white"
          >
            <option value="">— Choose pregnancy —</option>
            {activePregnancies.map(p => (
              <option key={p.pregnancyId} value={p.pregnancyId}>EDD: {p.edd} — G{p.gravidity}P{p.parity}</option>
            ))}
          </select>
          <button onClick={() => setShowObsPicker(false)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
      )}

      {/* Obs consultation form */}
      {s.showObsForm && s.obsFormPregnancyId && (
        <NewObsConsultForm
          patientId={patientId!}
          pregnancyId={s.obsFormPregnancyId}
          activePregnancies={activePregnancies}
          onSaved={(note) => {
            setNotes(prev => [note, ...prev]);
            dispatch({ type: 'CLOSE_OBS_FORM' });
          }}
          onCancel={() => dispatch({ type: 'CLOSE_OBS_FORM' })}
        />
      )}

      {/* ── Gynae Notes ── */}
      <div id="section-gynae-notes" data-nav-section className="mb-8">
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Stethoscope className="h-4 w-4" /> Gynae Notes ({gynaecNotes.length})
        </h2>
        {gynaecNotes.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No gynae notes yet.</p>
        ) : (
          <div className="space-y-3">
            {gynaecNotes.map(note => (
              <NoteCard
                key={note.id}
                note={note}
                isDoctor={isDoctor}
                editingNoteId={s.editingNoteId}
                editingNoteData={s.editingNoteData}
                editSaving={s.editSaving}
                setEditingNoteId={(id: string | null) => id !== null ? dispatch({ type: 'START_EDIT_NOTE', id, data: {} }) : dispatch({ type: 'CANCEL_EDIT_NOTE' })}
                setEditingNoteData={(v: React.SetStateAction<Partial<ClinicalNote>>) => dispatch({ type: 'UPDATE_EDIT_NOTE_DATA', data: typeof v === 'function' ? v(s.editingNoteData) : v })}
                onSaveNoteEdit={handleSaveNoteEdit}
                onGenerateInvoice={handleGenerateInvoice}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Obstetric Consultations ── */}
      <div id="section-obstetric-notes" data-nav-section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
            <Baby className="h-4 w-4" /> Obstetric Consultations ({obstetricNotes.length})
          </h2>
          <button
            onClick={() => navigate(`/patients/${patientId}/pregnancies`)}
            className="text-xs text-teal-600 hover:text-teal-800 font-medium"
          >
            Manage Pregnancies &rarr;
          </button>
        </div>
        {obstetricNotes.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No obstetric consultations yet.</p>
        ) : (
          <div className="space-y-2">
            {obstetricNotes.map(note => {
              const expanded = expandedObsNotes.has(note.id);
              const obs = note.obstetrics;
              return (
                <div key={note.id} className="bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 border-l-teal-500 p-4">
                  {/* Header + measurements — always visible */}
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleObsNote(note.id)}>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-800">Obstetric</span>
                      <span className="text-xs text-gray-500">{new Date(note.createdAt).toLocaleDateString()}</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </div>
                  {/* Compact measurements row */}
                  {obs && (
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600">
                      {obs.gestationWeeks != null && <span>{obs.gestationWeeks}w{obs.gestationDays ? ` ${obs.gestationDays}d` : ''}</span>}
                      {obs.sfh != null && <span>SFH: {obs.sfh}cm</span>}
                      {obs.bpSystolic != null && obs.bpDiastolic != null && <span>BP: {obs.bpSystolic}/{obs.bpDiastolic}</span>}
                      {obs.fetalHeartRate != null && <span>FHR: {obs.fetalHeartRate}bpm</span>}
                      {obs.presentation && <span className="capitalize">{obs.presentation}</span>}
                    </div>
                  )}
                  {/* Expanded SOAP content */}
                  {expanded && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {note.subjective && <div><span className="font-semibold text-gray-700">S:</span> <span className="text-gray-600">{note.subjective}</span></div>}
                        {note.objective && <div><span className="font-semibold text-gray-700">O:</span> <span className="text-gray-600">{note.objective}</span></div>}
                        {note.assessment && <div><span className="font-semibold text-gray-700">A:</span> <span className="text-gray-600">{note.assessment}</span></div>}
                        {note.plan && <div><span className="font-semibold text-gray-700">P:</span> <span className="text-gray-600">{note.plan}</span></div>}
                      </div>
                      {note.summary && <p className="mt-2 text-sm text-gray-500"><span className="font-semibold text-gray-700">Summary:</span> {note.summary}</p>}
                      {note.updatedAt && <p className="text-xs text-gray-400 mt-2">Edited {new Date(note.updatedAt).toLocaleDateString()}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Prescriptions */}
      <div id="section-prescriptions" data-nav-section>
      <PrescriptionSection
        prescriptions={prescriptions}
        isDoctor={isDoctor}
        showRxForm={s.showRxForm}
        setShowRxForm={(v: boolean) => v ? dispatch({ type: 'OPEN_RX_FORM' }) : dispatch({ type: 'CLOSE_RX_FORM' })}
        rxDrug={s.rxDrug}
        setRxDrug={(v: string) => dispatch({ type: 'SET_RX_FIELD', field: 'rxDrug', value: v })}
        rxDose={s.rxDose}
        setRxDose={(v: string) => dispatch({ type: 'SET_RX_FIELD', field: 'rxDose', value: v })}
        rxFreq={s.rxFreq}
        setRxFreq={(v: string) => dispatch({ type: 'SET_RX_FIELD', field: 'rxFreq', value: v })}
        rxDuration={s.rxDuration}
        setRxDuration={(v: string) => dispatch({ type: 'SET_RX_FIELD', field: 'rxDuration', value: v })}
        rxQty={s.rxQty}
        setRxQty={(v: number) => dispatch({ type: 'SET_RX_NUMBER_FIELD', field: 'rxQty', value: v })}
        rxRepeats={s.rxRepeats}
        setRxRepeats={(v: number) => dispatch({ type: 'SET_RX_NUMBER_FIELD', field: 'rxRepeats', value: v })}
        formulary={formulary}
        editingRxId={s.editingRxId}
        setEditingRxId={(id: string | null) => id !== null ? dispatch({ type: 'START_EDIT_RX', id, data: {} }) : dispatch({ type: 'CANCEL_EDIT_RX' })}
        editingRxData={s.editingRxData}
        setEditingRxData={(v: React.SetStateAction<Partial<Prescription>>) => dispatch({ type: 'UPDATE_EDIT_RX_DATA', data: typeof v === 'function' ? v(s.editingRxData) : v })}
        editSaving={s.editSaving}
        pdfLoading={pdfLoading}
        pdfError={pdfError}
        setPdfError={setPdfError}
        onCreatePrescription={handleCreatePrescription}
        onGeneratePdf={handleGeneratePdf}
        onSaveRxEdit={handleSaveRxEdit}
        onDeletePrescription={async (id) => { await del(`/clinical/${patientId}/prescriptions/${id}`); setPrescriptions(prev => prev.filter(p => p.id !== id)); }}
      />
      </div>

      {/* Surgeries */}
      <div id="section-surgeries" data-nav-section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
            <Scissors className="h-4 w-4" /> Surgeries ({surgeries.length})
          </h2>
          {isDoctor && (
            <button onClick={() => s.showSurgeryForm ? dispatch({ type: 'CLOSE_SURGERY_FORM' }) : dispatch({ type: 'OPEN_SURGERY_FORM' })} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Log Surgery
            </button>
          )}
        </div>

        {s.showSurgeryForm && (
          <SurgeryForm
            surgProcedure={s.surgProcedure} setSurgProcedure={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgProcedure', value: v })}
            surgHospital={s.surgHospital} setSurgHospital={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgHospital', value: v })}
            surgDate={s.surgDate} setSurgDate={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgDate', value: v })}
            surgEbl={s.surgEbl} setSurgEbl={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgEbl', value: v })}
            surgAnaesthesiaType={s.surgAnaesthesiaType} setSurgAnaesthesiaType={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgAnaesthesiaType', value: v })}
            surgAnaesthesiaOther={s.surgAnaesthesiaOther} setSurgAnaesthesiaOther={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgAnaesthesiaOther', value: v })}
            surgAnaesthetist={s.surgAnaesthetist} setSurgAnaesthetist={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgAnaesthetist', value: v })}
            surgCaseType={s.surgCaseType} setSurgCaseType={(v: '' | 'elective' | 'emergency') => dispatch({ type: 'SET_SURGERY_CASE_TYPE', value: v })}
            surgSurgicalAssistant={s.surgSurgicalAssistant} setSurgSurgicalAssistant={(v: string) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgSurgicalAssistant', value: v })}
            surgIndications={s.surgIndications} setSurgIndications={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgIndications', value: typeof v === 'function' ? v(s.surgIndications) : v })}
            surgProcedureNotes={s.surgProcedureNotes} setSurgProcedureNotes={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgProcedureNotes', value: typeof v === 'function' ? v(s.surgProcedureNotes) : v })}
            surgFindings={s.surgFindings} setSurgFindings={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgFindings', value: typeof v === 'function' ? v(s.surgFindings) : v })}
            surgComplications={s.surgComplications} setSurgComplications={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgComplications', value: typeof v === 'function' ? v(s.surgComplications) : v })}
            surgPostOp={s.surgPostOp} setSurgPostOp={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_SURGERY_FIELD', field: 'surgPostOp', value: typeof v === 'function' ? v(s.surgPostOp) : v })}
            surgIcd10={s.surgIcd10} setSurgIcd10={(v: CodeItem[]) => dispatch({ type: 'SET_SURGERY_ICD10', codes: v })}
            hospitals={hospitals}
            templates={templates}
            searchIcd10={searchIcd10}
            icd10Loading={icd10Loading}
            onSave={handleCreateSurgery}
            onCancel={() => dispatch({ type: 'CLOSE_SURGERY_FORM' })}
          />
        )}

        {surgeries.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No surgeries recorded.</p>
        ) : (
          <div className="space-y-2">
            {surgeries.map(surg => (
              <SurgeryCard
                key={surg.id}
                surgery={surg}
                isDoctor={isDoctor}
                editingSurgId={s.editingSurgId}
                editingSurgData={s.editingSurgData}
                editSaving={s.editSaving}
                setEditingSurgId={(id: string | null) => id !== null ? dispatch({ type: 'START_EDIT_SURGERY', id, data: {} }) : dispatch({ type: 'CANCEL_EDIT_SURGERY' })}
                setEditingSurgData={(v: React.SetStateAction<Partial<Surgery>>) => dispatch({ type: 'UPDATE_EDIT_SURGERY_DATA', data: typeof v === 'function' ? v(s.editingSurgData) : v })}
                onSaveEdit={handleSaveSurgEdit}
                onDelete={async (id) => { await del(`/clinical/${patientId}/surgeries/${id}`); setSurgeries(prev => prev.filter(sg => sg.id !== id)); }}
              />
            ))}
          </div>
        )}
      </div>
      {/* Vaginal Deliveries */}
      <div id="section-deliveries" data-nav-section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
            <Baby className="h-4 w-4" /> Vaginal Deliveries ({deliveries.length})
          </h2>
          {patientBloodGroup ? (
            <span className="px-2.5 py-1 text-xs rounded-full bg-teal-50 text-teal-700 border border-teal-200">
              Blood Group: {patientBloodGroup}{patientRhFactor === 'positive' ? '+' : patientRhFactor === 'negative' ? '−' : ''} {patientRhFactor ? `(Rh ${patientRhFactor === 'positive' ? 'Positive' : 'Negative'})` : ''}
            </span>
          ) : (
            <button
              onClick={() => navigate(`/patients/${patientId}`)}
              className="px-2.5 py-1 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            >
              Blood group not recorded — add in patient profile
            </button>
          )}
          {isDoctor && (
            <button
              onClick={() => s.showDeliveryForm ? dispatch({ type: 'CLOSE_DELIVERY_FORM' }) : dispatch({ type: 'OPEN_DELIVERY_FORM' })}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Log Delivery
            </button>
          )}
        </div>

        {s.showDeliveryForm && (
          <VaginalDeliveryForm
            vdDate={s.vdDate} setVdDate={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdDate', value: v })}
            vdTime={s.vdTime} setVdTime={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdTime', value: v })}
            vdHospital={s.vdHospital} setVdHospital={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdHospital', value: v })}
            vdDoctorName={s.vdDoctorName} setVdDoctorName={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdDoctorName', value: v })}
            vdDoctorEmail={s.vdDoctorEmail} setVdDoctorEmail={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdDoctorEmail', value: v })}
            vdPaediatrician={s.vdPaediatrician} setVdPaediatrician={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdPaediatrician', value: v })}
            vdWardSister={s.vdWardSister} setVdWardSister={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdWardSister', value: v })}
            vdUmbilicalCordPh={s.vdUmbilicalCordPh} setVdUmbilicalCordPh={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdUmbilicalCordPh', value: v })}
            vdDeliveryMode={s.vdDeliveryMode} setVdDeliveryMode={(v: DeliveryMode) => dispatch({ type: 'SET_DELIVERY_MODE', value: v })}
            vdUterotonic={s.vdUterotonic} setVdUterotonic={(v: UterotonicOption) => dispatch({ type: 'SET_DELIVERY_UTEROTONIC', value: v })}
            vdUterotonicOther={s.vdUterotonicOther} setVdUterotonicOther={(v: string) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdUterotonicOther', value: v })}
            vdHistory={s.vdHistory} setVdHistory={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdHistory', value: typeof v === 'function' ? v(s.vdHistory) : v })}
            vdNotes={s.vdNotes} setVdNotes={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdNotes', value: typeof v === 'function' ? v(s.vdNotes) : v })}
            vdPostPartum={s.vdPostPartum} setVdPostPartum={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_DELIVERY_FIELD', field: 'vdPostPartum', value: typeof v === 'function' ? v(s.vdPostPartum) : v })}
            hospitals={hospitals}
            babyWeight={s.vdBabyWeight} setVdBabyWeight={(v: number | null) => dispatch({ type: 'SET_DELIVERY_BABY_WEIGHT', value: v })}
            babySex={s.vdBabySex} setVdBabySex={(v: BabySex) => dispatch({ type: 'SET_DELIVERY_BABY_SEX', value: v })}
            apgar1min={s.vdApgar1min} setVdApgar1={(v: number | null) => dispatch({ type: 'SET_DELIVERY_APGAR1', value: v })}
            apgar5min={s.vdApgar5min} setVdApgar5={(v: number | null) => dispatch({ type: 'SET_DELIVERY_APGAR5', value: v })}
            bcgGiven={s.vdBcgGiven} setVdBcgGiven={(v: boolean) => dispatch({ type: 'SET_DELIVERY_BCG', value: v })}
            polioGiven={s.vdPolioGiven} setVdPolioGiven={(v: boolean) => dispatch({ type: 'SET_DELIVERY_POLIO', value: v })}
            saving={s.deliverySaving}
            onSave={handleCreateDelivery}
            onCancel={() => dispatch({ type: 'RESET_DELIVERY_FORM' })}
          />
        )}

        {deliveries.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No vaginal deliveries recorded.</p>
        ) : (
          <div className="space-y-2">
            {deliveries.map(d => (
              <VaginalDeliveryCard
                key={d.id}
                delivery={d}
                isDoctor={isDoctor}
                editingId={s.editingDeliveryId}
                editingData={s.editingDeliveryData}
                editSaving={s.editSaving}
                setEditingId={(id: string | null) => id !== null ? dispatch({ type: 'START_EDIT_DELIVERY', id, data: {} }) : dispatch({ type: 'CANCEL_EDIT_DELIVERY' })}
                setEditingData={(v: React.SetStateAction<Partial<VaginalDelivery>>) => dispatch({ type: 'UPDATE_EDIT_DELIVERY_DATA', data: typeof v === 'function' ? v(s.editingDeliveryData) : v })}
                onSaveEdit={handleSaveDeliveryEdit}
                onDelete={async (id) => {
                  await del(`/clinical/${patientId}/vaginal-deliveries/${id}`);
                  setDeliveries(prev => prev.filter(v => v.id !== id));
                }}
                onGeneratePdf={handleGenerateDeliveryPdf}
                pdfLoadingId={deliveryPdfLoadingId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Clinical Reports */}
      <div id="section-reports" data-nav-section>
      <ReportSection
        reports={reports}
        surgeries={surgeries}
        isDoctor={isDoctor}
        patientId={patientId || ''}
        showReportForm={s.showReportForm}
        setShowReportForm={(v: boolean) => v ? dispatch({ type: 'OPEN_REPORT_FORM' }) : dispatch({ type: 'CLOSE_REPORT_FORM' })}
        reportType={s.reportType}
        setReportType={(v: string) => dispatch({ type: 'SET_REPORT_FIELD', field: 'reportType', value: v })}
        reportFindings={s.reportFindings}
        setReportFindings={(v: React.SetStateAction<string>) => dispatch({ type: 'SET_REPORT_FIELD', field: 'reportFindings', value: typeof v === 'function' ? v(s.reportFindings) : v })}
        reportDraft={s.reportDraft}
        setReportDraft={(v: string) => dispatch({ type: 'SET_REPORT_FIELD', field: 'reportDraft', value: v })}
        reportDrafting={s.reportDrafting}
        reportSaving={s.reportSaving}
        onDraftReport={handleDraftReport}
        onSaveReport={handleSaveReport}
        onReportsChanged={setReports}
      />
      </div>

        </div>{/* end right content column */}
      </div>{/* end two-column flex */}

      {showNewPregnancyModal && (
        <NewPregnancyModal onSave={handleCreatePregnancy} onCancel={() => setShowNewPregnancyModal(false)} />
      )}
    </main>
  );
}
